import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import './ColorPalette.css';

/**
 * Extracts N dominant colors from an image URL using a hidden canvas.
 * Uses k-means-like color bucketing for speed.
 */
function extractColors(imageUrl, numColors = 5, proxyBase = null) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const tryExtract = () => {
      const canvas = document.createElement('canvas');
      const MAX = 100; // sample at 100px for speed
      const ratio = img.naturalWidth / img.naturalHeight;
      canvas.width = MAX;
      canvas.height = Math.round(MAX / ratio) || MAX;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let pixels;
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        pixels = data;
      } catch (e) {
        resolve([]);
        return;
      }

      // Collect all RGB values (skip near-white / near-black extremes for better palette)
      const colorMap = {};
      const BUCKET = 24; // bucket size for color quantization
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];
        if (a < 200) continue; // skip transparent
        // Skip near-black and near-white for a more interesting palette
        const brightness = (r + g + b) / 3;
        if (brightness < 20 || brightness > 240) continue;
        const br = Math.round(r / BUCKET) * BUCKET;
        const bg = Math.round(g / BUCKET) * BUCKET;
        const bb = Math.round(b / BUCKET) * BUCKET;
        const key = `${br},${bg},${bb}`;
        colorMap[key] = (colorMap[key] || 0) + 1;
      }

      // Sort by frequency
      const sorted = Object.entries(colorMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 50); // top 50 buckets

      // Pick diverse colors by spacing out — skip if too similar to already-picked
      const picked = [];
      for (const [key] of sorted) {
        if (picked.length >= numColors) break;
        const [r, g, b] = key.split(',').map(Number);
        const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        // Check distance from already picked
        const tooClose = picked.some(p => {
          const [pr, pg, pb] = p.rgb;
          return Math.abs(pr - r) < 40 && Math.abs(pg - g) < 40 && Math.abs(pb - b) < 40;
        });
        if (!tooClose) {
          picked.push({ hex, rgb: [r, g, b] });
        }
      }

      resolve(picked.map(p => p.hex));
    };

    img.onload = tryExtract;
    img.onerror = () => {
      // If direct load fails and we have a proxy, try via proxy
      if (proxyBase && !img.src.includes('/proxy-image')) {
        img.src = `${proxyBase}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      } else {
        resolve([]);
      }
    };

    img.src = imageUrl;
  });
}

/** Luminance for text contrast */
function getLuminance(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export default function ColorPalette({ imageUrl }) {
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedHex, setCopiedHex] = useState(null);
  const prevUrl = useRef(null);

  const extract = useCallback(async (url) => {
    if (!url) return;
    setLoading(true);
    setColors([]);
    const result = await extractColors(url, 5, API_URL);
    setColors(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (imageUrl && imageUrl !== prevUrl.current) {
      prevUrl.current = imageUrl;
      extract(imageUrl);
    }
  }, [imageUrl, extract]);

  const handleCopy = async (hex) => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopiedHex(hex);
      setTimeout(() => setCopiedHex(null), 1500);
    } catch {
      // Clipboard not available
    }
  };

  if (!imageUrl || (!loading && colors.length === 0)) return null;

  return (
    <div className="color-palette fade-in">
      <div className="color-palette-label">🎨 Color Palette</div>
      <div className="color-palette-swatches">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="color-swatch color-swatch--skeleton" />
          ))
        ) : (
          colors.map((hex) => {
            const isCopied = copiedHex === hex;
            const textColor = getLuminance(hex) > 0.5 ? '#111' : '#fff';
            return (
              <button
                key={hex}
                className={`color-swatch${isCopied ? ' color-swatch--copied' : ''}`}
                style={{ background: hex, '--swatch-text': textColor }}
                onClick={() => handleCopy(hex)}
                title={`Copy ${hex}`}
                aria-label={`Copy color ${hex}`}
              >
                <span className="color-swatch-hex">
                  {isCopied ? '✅' : hex}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
